import { HTMLAttributes, useEffect, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { useKey, useMedia } from 'react-use'
import Link from 'next/link'

import Logo from '@/components/base/logo'
import Reveal from '@/components/base/reveal'
import Typography from '@/components/base/typography'
import { CardsMenu, MobileMenu } from '@/components/layout/menu'
import { useStore } from '@/contexts/store'
import { useLayoutStore } from '@/store/layout'
import { useLoaderStore } from '@/store/loader'

import Burger from '../base/burger'
import { headerRevealAnimation } from './animations'
import { cn } from '@/lib/utils'

export type HeaderProps = HTMLAttributes<HTMLElement>

const Header = ({ ...props }: HeaderProps) => {
  const { strings } = useStore()
  const { labels, links } = strings || {}
  const menu = useRef<HTMLDivElement>(null)
  const book = useRef<HTMLDivElement>(null)
  const needsMobileMenu = useMedia('(max-width: 767px)', false)
  const menuOpen = useLayoutStore((state) => state.menuOpen)
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)
  const viewLogo = useLayoutStore((state) => state.viewLogo)

  useKey('Escape', () => setLayoutState({ menuOpen: false }))

  useEffect(
    () => setLayoutState({ lockScroll: menuOpen }),
    [menuOpen, setLayoutState]
  )

  useEffect(() => {
    const unsubscribeCanPOlay = useLoaderStore.subscribe(
      (state) => state.heroCanPlay,
      (heroCanPlay) => {
        if (!needsMobileMenu && !heroCanPlay) {
          document.documentElement.classList.remove('ui-reveal')

          setLayoutState({ menuOpen: false })
        }
      }
    )

    const unsubscribePoster = useLoaderStore.subscribe(
      (state) => state.isPosterReady,
      (isPosterReady) => {
        if (needsMobileMenu && !isPosterReady) {
          document.documentElement.classList.remove('ui-reveal')

          setLayoutState({ menuOpen: false })
        }
      }
    )

    return () => {
      unsubscribeCanPOlay()
      unsubscribePoster()
    }
  }, [setLayoutState, needsMobileMenu])

  return (
    <>
      <Reveal animation={headerRevealAnimation}>
        {(revealRef) => (
          <header
            ref={revealRef}
            className={cn(
              'fixed left-margin right-margin top-[2rem] z-header flex items-center justify-between rounded-[0.8rem] bg-black pl-[1rem]',
              'md:static md:left-auto md:right-auto md:top-auto md:rounded-none md:bg-none md:pl-0'
            )}
            {...props}
          >
            <div className="hidden md:fixed md:left-[3rem] md:top-[4.3rem] md:z-header md:flex lg:left-[4.5rem]">
              <div
                className={cn(
                  'overflow-hidden',
                  'lg:transition-opacity lg:duration-500 lg:ease-in-out-cubic'
                  // 'lg:opacity-0 lg:[.ui-reveal_&]:opacity-100'
                )}
              >
                <Typography
                  className="flex text-[1.28rem] capitalize tracking-[0.0384rem] text-white/50"
                  data-motion-place
                >
                  {labels?.place}
                </Typography>
              </div>
            </div>

            <div className="relative md:fixed md:left-1/2 md:top-[4rem] md:z-header md:-translate-x-1/2">
              <div
                className={cn(
                  'lg:transition-opacity lg:duration-500 lg:ease-in-out-cubic',
                  'lg:opacity-0 lg:[.ui-reveal_&]:opacity-100',
                  {
                    'lg:!opacity-100 lg:duration-1000': menuOpen || viewLogo
                  }
                )}
              >
                <Link
                  href="/"
                  scroll={false}
                  className="-m-[1rem] flex p-[1rem]"
                >
                  <Logo />
                </Link>
              </div>
            </div>

            <div className="md:fixed md:right-[3rem] md:top-[3rem] md:z-header lg:right-[4rem]">
              <div className={cn('flex items-center md:space-x-[5rem]')}>
                <div
                  className={cn(
                    'lg:transition-opacity lg:duration-500 lg:ease-in-out-cubic',
                    'lg:opacity-0 lg:[.ui-reveal_&]:opacity-100',
                    {
                      'lg:!opacity-100 lg:duration-1000': menuOpen || viewLogo
                    }
                  )}
                >
                  <div ref={book} className="hidden md:flex">
                    <Link
                      scroll={false}
                      href={links?.book.url}
                      target={links?.book.target || '_self'}
                      className="link -m-[1rem] flex p-[1rem]"
                      data-gtm-event="book-cta"
                    >
                      <Typography className="pointer-events-none text-[1rem] font-bold uppercase leading-[1.2] tracking-[0.05rem]">
                        {links?.book.title}
                      </Typography>
                    </Link>
                  </div>
                </div>

                <div className="inline-flex overflow-hidden">
                  <Burger
                    transitionDuration={1000}
                    labelClose={labels?.close}
                    labelMenu={labels?.menu}
                  />
                </div>
              </div>
            </div>
          </header>
        )}
      </Reveal>

      <Transition
        nodeRef={menu}
        in={menuOpen}
        timeout={{
          enter: 0,
          exit: 1000
        }}
        unmountOnExit
      >
        {(state) =>
          needsMobileMenu ? (
            <MobileMenu ref={menu} state={state} />
          ) : (
            <CardsMenu ref={menu} state={state} />
          )
        }
      </Transition>
    </>
  )
}

export default Header
